/**
 * Hash router — the address bar and the store, kept in both directions.
 *
 * WHY HASH, NOT THE HISTORY API: the built `dist/` must work when it is
 * opened straight from disk or shared as a folder, with no server rewrite
 * rule, and on any static host. `location.hash` needs none of that, and it
 * needs no router dependency — parsing a string plus a `hashchange`
 * listener is the whole mechanism.
 *
 * THE ONE SOURCE OF TRUTH IS `ui.route` IN THE STORE (src/state/store.ts).
 * The hash is a VIEW of it:
 *
 *   hash → store   `hashchange` (and boot) parse the hash and, only when it
 *                  actually differs, adopt the route (+ the selected entity
 *                  for `#/play/characters/:id`) in ONE state update;
 *   store → hash   a subscription on the store serializes the route and
 *                  writes it back when it differs. Because our own writes
 *                  re-enter the hash handler with a route that already
 *                  matches, they settle instead of looping, and navigating
 *                  never pushes a duplicate history entry.
 *
 * There is deliberately no second navigation state: `navigate()` stays the
 * one way to move, and a hash the app writes is always
 * `serializeRoute(state.ui.route, state.ui.selectedId)`.
 *
 * FALLBACKS NEVER THROW: an empty hash, `#/`, or anything unknown parses to
 * the default view (an unknown `#/play/…` segment stays on the player home —
 * a player URL must never bounce onto the GM chrome). Unknown hashes are
 * NOT rewritten in place, so the browser's back/forward across hash changes
 * keeps working.
 *
 * SURFACE + STORAGE: the route prefix decides which local-storage namespace
 * is active (see the module comment in persistence/storage.ts): `#/play…`
 * → `atla-player:*`, everything else → `atla-gm:*`. On boot the prefix picks
 * the namespace before anything is read; a runtime crossing flushes the
 * outgoing autosave first, swaps the namespace and reloads — done inside
 * the store subscription (onStoreChange) so it happens exactly when the
 * route crosses, whoever moved it (a click or the back button).
 *
 * `window` is touched only inside the exported functions — importing this
 * module (e.g. from a Node gate or a server render) must stay side-effect
 * free.
 */
import {
  DEFAULT_ROUTE,
  getState,
  setUiState,
  subscribe,
  type AppRoute,
  type PlayerRouteId,
  type RouteId,
} from '@/state/store';
import { syncStorageSurface, type StorageSurface } from '@/persistence/storage';

/**
 * Every GM view id — the bare `#/<viewId>` vocabulary — in `RouteId` order,
 * plus `grid` (the battle grid's hash form is parameterized: `#/grid` for
 * the chooser, `#/grid/:mapId` view-only) and `items` (the bare `#/items`
 *): both are registered by THIS file rather than by the store's `RouteId`
 * (src/state/** belongs to another lane). Adding them here keeps the one
 * property that matters: the GM shell's nav and the router share a single
 * vocabulary.
 */
export const GM_ROUTE_IDS: readonly (RouteId | GridRoute | ItemsRoute | TableRoute)[] = [
  'cockpit',
  'sessions',
  'characters',
  'party',
  'table',
  'items',
  'npcs',
  'forge',
  'notes',
  'encounters',
  'world',
  'mapforge',
  'story',
  'content',
  'rules',
  'reference',
  'gmscreen',
  'grid',
];

/**
 * The battle-grid route id, registered by this router (parsed below, kept
 * out of the store's `AppRoute` because src/state/** is another lane's
 * file). `RoutedView` is what this file actually speaks; `toAppRoute` is
 * the ONE documented narrowing back into the store's vocabulary.
 */
export type GridRoute = 'grid';

/**
 * The item-management route id, registered the same way as `grid`: the
 * bare `#/items` hash rides `ui.route` at runtime (see `toAppRoute` below)
 * and stays out of the store's `RouteId`.
 */
export type ItemsRoute = 'items';

/**
 * The battle table (`#/table`) — the GM's interactive map: drag-and-drop
 * figures with portraits and hit points, a ruler, area templates, the
 * initiative order and the broadcast controls. Registered here like `grid`.
 */
export type TableRoute = 'table';

/**
 * The broadcast screen (`#/cast`) — a chrome-less, read-only second window
 * the GM shares to Discord or puts on the table's TV. It is its OWN storage
 * surface (`atla-cast`, see persistence/storage.ts): it reads the GM's
 * document read-only and follows live edits over a BroadcastChannel, so it
 * can never write the GM's autosave. Not a nav entry.
 */
export type CastRoute = 'cast';

export type RoutedView = AppRoute | GridRoute | ItemsRoute | TableRoute | CastRoute;

/** Player surface? Derived from the id — the shell renders from this too. */
export function isPlayerRoute(route: RoutedView): route is PlayerRouteId {
  return route === 'play' || route.startsWith('play/');
}

/** The battle grid? (i.e. this hash addressed a map-capable grid route). */
export function isGridRoute(route: RoutedView): route is GridRoute {
  return route === 'grid';
}

/** The item manager? (the bare `#/items` route). */
export function isItemsRoute(route: RoutedView): route is ItemsRoute {
  return route === 'items';
}

/** The battle table? (the bare `#/table` route). */
export function isTableRoute(route: RoutedView): route is TableRoute {
  return route === 'table';
}

/** The broadcast screen? (`#/cast`) */
export function isCastRoute(route: RoutedView): route is CastRoute {
  return route === 'cast';
}

/** Absolute URL of the broadcast screen — what the GM opens as a second window. */
export function castUrl(): string {
  return `${window.location.origin}${window.location.pathname}#/cast`;
}

/**
 * The store writes `ui.route: AppRoute`, and `grid` / `items` ride that
 * same field at runtime. This is the only place the extra ids are narrowed
 * away — a plain assertion, because `AppRoute ⊆ RoutedView` makes it sound.
 */
function toAppRoute(route: RoutedView): AppRoute {
  return route as AppRoute;
}

/**
 * Navigate to the battle grid in ONE state update (route + selected map
 * together, so the hash settles on `#/grid/:mapId` without a transient
 * `#/grid` write). `null` opens the chooser.
 */
export function openBattleGrid(mapId: string | null): void {
  setUiState({ route: toAppRoute('grid'), selectedId: mapId });
}

/** Result of parsing a hash: the route plus the deep-linked entity id. */
export interface ParsedHash {
  route: RoutedView;
  selectedId: string | null;
}

const DEFAULT_HASH: ParsedHash = { route: DEFAULT_ROUTE, selectedId: null };

/**
 * Which surface a route belongs to (drives the storage namespace). The
 * prefix property is unchanged: `#/play…` → player, everything else —
 * including `#/grid…` — → GM, so a player route still cannot reach GM data.
 */
export function surfaceOf(route: RoutedView): StorageSurface {
  return isPlayerRoute(route) ? 'player' : isCastRoute(route) ? 'cast' : 'gm';
}

function isRouteId(value: string): value is RouteId | GridRoute | ItemsRoute | TableRoute {
  return (GM_ROUTE_IDS as readonly string[]).includes(value);
}

function safeDecode(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value; // malformed %-escape: keep the raw id, never throw
  }
}

/**
 * Parse `location.hash` into the store's vocabulary. Pure, total, never
 * throws: empty / `#` / `#/` → the default view; a known `#/<viewId>` →
 * that GM view; `#/grid` / `#/grid/:mapId` → the battle grid (with the map
 * id as selection); `#/play/…` → the player routes (with `:id` as
 * selection); anything else → the default view, except unknown `#/play/…`
 * segments, which fall back to the player home so a player URL never lands
 * on GM chrome.
 */
export function parseHash(hash: string): ParsedHash {
  try {
    const raw = hash.startsWith('#') ? hash.slice(1) : hash;
    const path = raw.startsWith('/') ? raw.slice(1) : raw;
    const segments = path.split('/').filter((s) => s !== '');
    if (segments.length === 0) return { ...DEFAULT_HASH };
    const head = segments[0] as string;
    if (head === 'play') return parsePlayerSegments(segments);
    if (head === 'grid') return parseGridSegments(segments);
    if (head === 'cast') return { route: 'cast', selectedId: null };
    if (segments.length === 1 && isRouteId(head)) {
      return { route: head, selectedId: null };
    }
    return { ...DEFAULT_HASH };
  } catch {
    return { ...DEFAULT_HASH };
  }
}

function parsePlayerSegments(segments: string[]): ParsedHash {
  if (segments.length === 1) return { route: 'play', selectedId: null };
  const sub = segments[1] as string;
  if (segments.length === 2 && (sub === 'rules' || sub === 'content' || sub === 'create')) {
    return { route: `play/${sub}` as PlayerRouteId, selectedId: null };
  }
  if (sub === 'characters') {
    // `#/play/characters` or `#/play/characters/:id` — extra junk after the
    // id is ignored rather than thrown away into a different surface.
    const id = segments.length >= 3 ? safeDecode(segments[2] as string) : null;
    if (id !== null && id !== '') return { route: 'play/characters', selectedId: id };
    return { route: 'play/characters', selectedId: null };
  }
  // Unknown player segment: stay on the player surface, never GM chrome.
  return { route: 'play', selectedId: null };
}

function parseGridSegments(segments: string[]): ParsedHash {
  // `#/grid` (the GM's chooser) or `#/grid/:mapId` (view-only). Extra
  // segments after the id are ignored — exactly like the player deep link —
  // and a malformed %-escape keeps the raw id instead of throwing.
  const id = segments.length >= 2 ? safeDecode(segments[1] as string) : null;
  return { route: 'grid', selectedId: id !== null && id !== '' ? id : null };
}

/**
 * Store → hash. The canonical form: `#/<route>` for every route, plus
 * `/<selectedId>` for a deep-linked player character and for the battle
 * grid's `#/grid/:mapId` (view-only) form. `#/` and `` both parse back to
 * the default view, so aliases survive the round trip.
 */
export function serializeRoute(route: RoutedView, selectedId: string | null): string {
  if (route === 'grid') {
    return selectedId !== null && selectedId !== ''
      ? `#/grid/${encodeURIComponent(selectedId)}`
      : '#/grid';
  }
  if (route === 'play/characters' && selectedId !== null && selectedId !== '') {
    return `#/play/characters/${encodeURIComponent(selectedId)}`;
  }
  return `#/${route}`;
}

/** Absolute URL of the player home — the string the GM's button copies. */
export function playerHomeUrl(): string {
  return `${window.location.origin}${window.location.pathname}#/play`;
}

/** Adopt a parsed hash into the store — in ONE update (no transient hashes). */
function applyToStore(parsed: ParsedHash): void {
  const current = getState().ui;
  if (parsed.route === current.route && parsed.selectedId === current.selectedId) return;
  // `grid` narrows here — the one documented cast (toAppRoute above).
  setUiState({ route: toAppRoute(parsed.route), selectedId: parsed.selectedId });
}

/**
 * The store → hash direction. Also the single place that reacts to a route
 * CROSSING the surface boundary: the storage namespace follows the route
 * (flush → swap → reload, see persistence/storage.ts).
 */
function onStoreChange(): void {
  const { route, selectedId } = getState().ui;
  syncStorageSurface(surfaceOf(route)); // no-op unless the surface changed
  const next = serializeRoute(route, selectedId);
  if (window.location.hash !== next) window.location.hash = next;
}

/**
 * Install the two-way sync. Called once from App.tsx's boot effect (and
 * re-called by the gates with a fake `window`). Returns the uninstaller —
 * StrictMode's mount/unmount/mount must leave exactly one router behind.
 */
export function installRouter(): () => void {
  // 1. Boot: the route PREFIX picks the storage namespace before anything
  //    is read from localStorage (App loads the autosave right after this).
  const boot = parseHash(window.location.hash);
  syncStorageSurface(surfaceOf(boot.route));

  // 2. The store adopts the URL. The subscription attaches AFTER this, so a
  //    boot on an unknown/garbage hash leaves the address bar untouched —
  //    no history entry is replaced before the user has navigated anywhere.
  applyToStore(boot);

  // 3. From here both directions are live.
  const unbind = subscribe(onStoreChange);
  const onHashChange = (): void => applyToStore(parseHash(window.location.hash));
  window.addEventListener('hashchange', onHashChange);
  return () => {
    unbind();
    window.removeEventListener('hashchange', onHashChange);
  };
}
